import { FUNCTIONAL_REACH_ASSESSMENT } from "../models/Enums";
import { reach_distance_metric } from "./functionalReach";
import { FunctionalReachStates, FunctionalReachAudios } from "./assessment_states";
import { ASSESSMENT_STATUSES } from "../movenet/params";

// Focal lengths for your device (in pixels)
const FX = 740;
// const FY = 740;

/**
 * Get adaptive keypoint indices based on pose model type
 * @param {Array} keypoints - Array of keypoints from pose detection
 * @returns {Object} Object containing keypoint indices for BlazePose (33) or MoveNet (17)
 */
export function getKeypointIndices(keypoints) {
  const isSinglePose = keypoints.length > 20; // BlazePose has 33 keypoints, MoveNet has 17
  return {
    isSinglePose,
    leftWrist: isSinglePose ? 15 : 9,
    rightWrist: isSinglePose ? 16 : 10,
    leftAnkle: isSinglePose ? 27 : 15,
    rightAnkle: isSinglePose ? 28 : 16,
    leftShoulder: isSinglePose ? 11 : 5,
    rightShoulder: isSinglePose ? 12 : 6,
    leftElbow: isSinglePose ? 13 : 7,
    rightElbow: isSinglePose ? 14 : 8
  };
}

// CRITICAL: Check foot stability and stop assessment if feet move
function checkFootStabilityAndStopIfMoved(functionalReach, leftAnkleCurr, leftAnkleRef, rightAnkleCurr, rightAnkleRef) {
  // SAFETY: If reference points don't exist yet, continue assessment (still in calibration)
  if (!leftAnkleRef || !rightAnkleRef) {
    return true; // Continue assessment
  }

  // LIVE CAMERA: Use more tolerant threshold for ankle stability
  const leftStable = isWithin50Pixels(leftAnkleCurr, leftAnkleRef);
  const rightStable = isWithin50Pixels(rightAnkleCurr, rightAnkleRef);
  const feetStable = leftStable && rightStable;

  if (!feetStable) {
    functionalReach.append_sentry_message("Assessment stopped - patient feet moved during reach");

    // Trigger assessment interruption
    functionalReach.current_state = FunctionalReachStates.assessment_interrupted;
    functionalReach.audio_player.play(FunctionalReachAudios.assessment_interrupted);
    functionalReach.handleAssessmentInterruption();

    // LIVE CAMERA: Set assessment error flag to trigger modal
    ASSESSMENT_STATUSES.assessment_error = true;

    // Stop tracking and complete assessment with current results
    functionalReach.track_patient = false;
    functionalReach.end_time = functionalReach.current_time;

    // LIVE CAMERA: Modal now triggered automatically by audio player

    return false; // Assessment should stop
  }

  return true; // Feet are stable, continue assessment
}

// If you know the real-world depth (distance from camera to subject in inches), set it here.
// Otherwise, set to 1 for proportional measurement.

// const DEFAULT_DEPTH_INCHES = 72; // Example: 72 inches from camera. Adjust as needed.

function selectPatientPose(poses, reachType) {
  if (!poses || poses.length === 0) return null;
  if (poses.length === 1) return poses[0];
  
  // Filter poses that have required keypoints AND are performing reach movement
  const reachingPoses = [];
  
  poses.forEach((pose, index) => {
    if (!pose || !pose.keypoints) return;
    
    // Check if this pose has required body parts for reach assessment
    const leftHip = pose.keypoints[11];
    const rightHip = pose.keypoints[12];
    const leftWrist = pose.keypoints[9];   // MoveNet left wrist
    const rightWrist = pose.keypoints[10]; // MoveNet right wrist
    
    if (!leftHip || !rightHip) {
      return;
    }
    
    if (!leftWrist && !rightWrist) {
      return;
    }
    
    // Check if this person is actively doing the reach movement
    const isStretching = isArmStretchedForPose(pose, reachType);
    
    if (isStretching) {
      reachingPoses.push({ pose, index });
    }
  });
  
  // If exactly one person is stretching = that's our patient
  if (reachingPoses.length === 1) {
    return reachingPoses[0].pose;
  }
  
  // If multiple people stretching, use closest to center among stretching people
  if (reachingPoses.length > 1) {
    return selectClosestToCenter(reachingPoses.map(p => p.pose));
  }
  
  // Fallback: no people stretching, use closest to center (maybe still in setup phase)
  return selectClosestToCenter(poses);
}

// TESTING: Helper to select closest pose to center (for fallback cases)
function selectClosestToCenter(poses) {
  if (!poses || poses.length === 0) return null;
  if (poses.length === 1) return poses[0];
  
  const frameCenterX = 960;
  const frameCenterY = 540;
  let minDistance = Infinity;
  let selectedPose = poses[0];
  
  poses.forEach((pose, index) => {
    if (pose && pose.keypoints) {
      const leftHip = pose.keypoints[11];
      const rightHip = pose.keypoints[12];
      
      if (leftHip && rightHip) {
        const hipCenterX = (leftHip.x + rightHip.x) / 2;
        const hipCenterY = (leftHip.y + rightHip.y) / 2;
        const distance = Math.sqrt(
          Math.pow(hipCenterX - frameCenterX, 2) + 
          Math.pow(hipCenterY - frameCenterY, 2)
        );
        
        if (distance < minDistance) {
          minDistance = distance;
          selectedPose = pose;
        }
      }
    }
  });
  
  return selectedPose;
}

// TESTING: Check if a specific pose has arm stretched (for individual pose evaluation)
function isArmStretchedForPose(pose, reachType) {
  if (!pose || !pose.keypoints) return false;

  const keypoints = pose.keypoints;
  const indices = getKeypointIndices(keypoints);
  let shoulder, elbow, wrist;

  if (reachType === FUNCTIONAL_REACH_ASSESSMENT.Left_Reach || reachType === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach) {
    shoulder = keypoints[indices.leftShoulder];
    elbow = keypoints[indices.leftElbow];
    wrist = keypoints[indices.leftWrist];
  } else if (reachType === FUNCTIONAL_REACH_ASSESSMENT.Right_Reach) {
    shoulder = keypoints[indices.rightShoulder];
    elbow = keypoints[indices.rightElbow];
    wrist = keypoints[indices.rightWrist];
  }
  
  if (!shoulder || !elbow || !wrist) return false;
  
  // Same stretch detection logic as original function
  const vectorA = {
    x: wrist.x - shoulder.x,
    y: wrist.y - shoulder.y,
  };
  const vectorB = { x: 0, y: 50 };
  const angle1 = getAngleBetweenVectors(vectorA, vectorB);
  const isStretched = angle1 >= 75 && angle1 <= 105;
  
  const vec1 = {
    x: shoulder.x - elbow.x,
    y: shoulder.y - elbow.y,
  };
  const vec2 = {
    x: wrist.x - elbow.x,
    y: wrist.y - elbow.y,
  };
  const elbowAngle = getAngleBetweenVectors(vec1, vec2);
  const isArmStraight = elbowAngle >= 160 && elbowAngle <= 200;
  
  return isStretched && isArmStraight;
}

export function recordLeftReachDistance(functionalReach, poses) {
  let wristRefPoint = null;
  let wristCurrPoint = null;

  // TESTING: Add safety checks for undefined poses/keypoints
  if (!poses || poses.length === 0 || !poses[0] || !poses[0].keypoints) {
    return;
  }

  // OPTIMIZED: Use existing smart pose selection function instead of duplicate logic
  const selectedPose = selectPatientPose(poses, FUNCTIONAL_REACH_ASSESSMENT.Left_Reach);
  if (!selectedPose) {
    return;
  }

  const keypoints = selectedPose.keypoints;
  const indices = getKeypointIndices(keypoints);
  const { leftWrist: leftWristIdx, leftAnkle: leftAnkleIdx, rightAnkle: rightAnkleIdx } = indices;
  
  // Check if required keypoints exist
  if (!keypoints[leftWristIdx] || !keypoints[leftAnkleIdx] || !keypoints[rightAnkleIdx]) {
    return;
  }

  wristRefPoint = functionalReach.left_reference_point?.leftWrist;
  wristCurrPoint = { x: keypoints[leftWristIdx].x, y: keypoints[leftWristIdx].y };

  const leftAnkleCurr = { x: keypoints[leftAnkleIdx].x, y: keypoints[leftAnkleIdx].y };
  const leftAnkleRef = functionalReach.left_reference_point?.leftAnkle;
  const rightAnkleCurr = { x: keypoints[rightAnkleIdx].x, y: keypoints[rightAnkleIdx].y };
  const rightAnkleRef = functionalReach.left_reference_point?.rightAnkle;

  functionalReach.arom_left_high_text.textContent = "Left";

  functionalReach.arom_left_high_value.innerText = "Reach";

  const calc_distance = calc_distance_in_inches(
    wristRefPoint,
    wristCurrPoint,
    functionalReach.averaged_depth
  ).toFixed(2);

  // Add safety check before accessing wristRefPoint.x
  if (!wristRefPoint) {
    functionalReach.arom_left_low_text.textContent = "0";
    return;
  }

  // CRITICAL: Check foot stability - STOP assessment if feet move
  if (!checkFootStabilityAndStopIfMoved(functionalReach, leftAnkleCurr, leftAnkleRef, rightAnkleCurr, rightAnkleRef)) {
    return; // Assessment was stopped due to foot movement
  }

  // TESTING: Check if arm is stretched before updating UI
  const armStretched = isArmStretched(poses, FUNCTIONAL_REACH_ASSESSMENT.Left_Reach);
  const currentMax = parseFloat(reach_distance_metric.leftReach) || 0;
  const currentDistance = parseFloat(calc_distance) || 0;

  // Only update if arm is properly stretched (horizontal position)
  if (armStretched && currentDistance > currentMax) {
    functionalReach.arom_left_low_text.textContent = calc_distance;
    reach_distance_metric.leftReach = currentDistance;
    functionalReach.arom_left_low_value.innerText = "inches";
  } else if (!armStretched) {
    functionalReach.arom_left_low_text.textContent = `${currentMax.toFixed(2)}`;
  } else {
    functionalReach.arom_left_low_text.textContent = `${currentMax.toFixed(2)}`;
  }
  functionalReach.arom_left_low_value.innerText = "inches";
}

export function recordRightReachDistance(functionalReach, poses) {
  let wristRefPoint = null;
  let wristCurrPoint = null;

  // TESTING: Add safety checks for undefined poses/keypoints
  if (!poses || poses.length === 0 || !poses[0] || !poses[0].keypoints) {
    return;
  }

  // OPTIMIZED: Use existing smart pose selection function instead of duplicate logic
  const selectedPose = selectPatientPose(poses, FUNCTIONAL_REACH_ASSESSMENT.Right_Reach);
  if (!selectedPose) {
    return;
  }

  const keypoints = selectedPose.keypoints;
  const indices = getKeypointIndices(keypoints);
  const { rightWrist: rightWristIdx, leftAnkle: leftAnkleIdx, rightAnkle: rightAnkleIdx } = indices;
  
  // Check if required keypoints exist
  if (!keypoints[rightWristIdx] || !keypoints[leftAnkleIdx] || !keypoints[rightAnkleIdx]) {
    return;
  }

  wristRefPoint = functionalReach.right_reference_point?.rightWrist;
  wristCurrPoint = { x: keypoints[rightWristIdx].x, y: keypoints[rightWristIdx].y };

  const leftAnkleCurr = { x: keypoints[leftAnkleIdx].x, y: keypoints[leftAnkleIdx].y };
  const leftAnkleRef = functionalReach.right_reference_point?.leftAnkle;
  const rightAnkleCurr = { x: keypoints[rightAnkleIdx].x, y: keypoints[rightAnkleIdx].y };
  const rightAnkleRef = functionalReach.right_reference_point?.rightAnkle;

  functionalReach.arom_right_high_text.textContent = "Right";
  functionalReach.arom_right_high_value.innerText = "Reach";

  const calc_distance = calc_distance_in_inches(
    wristRefPoint,
    wristCurrPoint,
    functionalReach.averaged_depth
  ).toFixed(2);
  
  // Add safety check before accessing wristRefPoint.x
  if (!wristRefPoint) {
    functionalReach.arom_right_low_text.textContent = "0";
    return;
  }

  // CRITICAL: Check foot stability - STOP assessment if feet move
  if (!checkFootStabilityAndStopIfMoved(functionalReach, leftAnkleCurr, leftAnkleRef, rightAnkleCurr, rightAnkleRef)) {
    return; // Assessment was stopped due to foot movement
  }

  // TESTING: Check if arm is stretched before updating UI
  const armStretched = isArmStretched(poses, FUNCTIONAL_REACH_ASSESSMENT.Right_Reach);
  const currentMax = parseFloat(reach_distance_metric.rightReach) || 0;
  const currentDistance = parseFloat(calc_distance) || 0;
  
  // Only update if arm is properly stretched (horizontal position)
  if (armStretched && currentDistance > currentMax) {
    functionalReach.arom_right_low_text.textContent = calc_distance;
    reach_distance_metric.rightReach = currentDistance;
    functionalReach.arom_right_low_value.innerText = "inches";
  } else if (!armStretched) {
    functionalReach.arom_right_low_text.textContent = `${currentMax.toFixed(2)}`;
  } else {
    functionalReach.arom_right_low_text.textContent = `${currentMax.toFixed(2)}`;
  }

  functionalReach.arom_right_low_value.innerText = "inches";
}

export function recordForwardReachDistance(functionalReach, poses) {
  if (!poses || poses.length === 0 || !poses[0] || !poses[0].keypoints) {
    return;
  }

  const selectedPose = selectPatientPose(poses, FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach);
  if (!selectedPose) {
    return;
  }

  const keypoints = selectedPose.keypoints;
  const indices = getKeypointIndices(keypoints);
  const { leftWrist: leftWristIdx, rightWrist: rightWristIdx, leftAnkle: leftAnkleIdx, rightAnkle: rightAnkleIdx } = indices;

  functionalReach.arom_right_high_text.textContent = "Forward";
  functionalReach.arom_right_high_value.innerText = "Reach";

  // Check if patient is in profile position (required for all measurements)
  const isInProfile = isPatientInProfilePosition(keypoints, functionalReach.baseline_shoulder_width);

  // Start with current maximum
  const currentMax = parseFloat(reach_distance_metric.forwardReach) || 0;
  let newMaxDistance = currentMax;

  // Get current arm stretch status
  const armStatus = getStretchedArmForForwardReach([selectedPose]);

  // DEBUG: Only log when arm status changes
  if (!functionalReach.lastArmStatus ||
      functionalReach.lastArmStatus.left !== armStatus.left ||
      functionalReach.lastArmStatus.right !== armStatus.right ||
      functionalReach.lastIsInProfile !== isInProfile) {
    // functionalReach.append_sentry_message(`Forward Reach: armStatus.left=${armStatus.left}, armStatus.right=${armStatus.right}, isInProfile=${isInProfile}`);
    functionalReach.lastArmStatus = { left: armStatus.left, right: armStatus.right };
    functionalReach.lastIsInProfile = isInProfile;
  }

  // TRACK LEFT ARM (if reference exists)
  const leftRefExists = !!functionalReach.forward_left_reference_point;
  if (functionalReach.lastLeftRefCheck === undefined || functionalReach.lastLeftRefCheck !== leftRefExists) {
    // functionalReach.append_sentry_message(`Left arm check: reference exists=${leftRefExists}, leftWrist exists=${!!keypoints[leftWristIdx]}`);
    functionalReach.lastLeftRefCheck = leftRefExists;
  }

  if (functionalReach.forward_left_reference_point && keypoints[leftWristIdx]) {
    if (!functionalReach.leftArmBlockEntered) {
      // functionalReach.append_sentry_message(`Entering left arm block: armStatus.left=${armStatus.left}, isInProfile=${isInProfile}`);
      functionalReach.leftArmBlockEntered = true;
    }

    // Only measure if LEFT arm is currently stretched AND patient in profile
    if (armStatus.left && isInProfile) {
      // Forward reach: Only check VISIBLE ankle (left) for stability, not right (occluded in profile)
      const leftAnkleCurr = keypoints[leftAnkleIdx];
      const leftAnkleRef = functionalReach.forward_left_reference_point.leftAnkle;

      // Check if left ankle moved (right ankle ignored - may be occluded)
      if (leftAnkleCurr && leftAnkleRef && isWithin50Pixels(leftAnkleCurr, leftAnkleRef)) {
        // Calculate LEFT wrist distance
        const leftWrist = keypoints[leftWristIdx];
        const leftPixelDiff = Math.abs(leftWrist.x - functionalReach.forward_left_reference_point.leftWrist.x);
        const leftDistance = (leftPixelDiff * functionalReach.averaged_depth) / FX;

        // Update max if this is better
        if (leftDistance > newMaxDistance) {
          newMaxDistance = leftDistance;
        }
      }
    }
  }

  // TRACK RIGHT ARM (if reference exists) - INDEPENDENT of left arm
  // Only log reference check when status changes
  const rightRefExists = !!functionalReach.forward_right_reference_point;
  if (functionalReach.lastRightRefCheck === undefined || functionalReach.lastRightRefCheck !== rightRefExists) {
    // functionalReach.append_sentry_message(`Right arm check: reference exists=${rightRefExists}, rightWrist exists=${!!keypoints[rightWristIdx]}`);
    functionalReach.lastRightRefCheck = rightRefExists;
  }

  if (functionalReach.forward_right_reference_point && keypoints[rightWristIdx]) {
    // Only log entry once per arm stretch session
    if (!functionalReach.rightArmBlockEntered) {
      // functionalReach.append_sentry_message(`Entering right arm block: armStatus.right=${armStatus.right}, isInProfile=${isInProfile}`);
      functionalReach.rightArmBlockEntered = true;
    }

    // Only measure if RIGHT arm is currently stretched AND patient in profile
    if (armStatus.right && isInProfile) {
      // Forward reach: Only check VISIBLE ankle (left) for stability, not right (occluded in profile)
      const leftAnkleCurr = keypoints[leftAnkleIdx];
      const leftAnkleRef = functionalReach.forward_right_reference_point.leftAnkle;

      const ankleCheck = leftAnkleCurr && leftAnkleRef && isWithin50Pixels(leftAnkleCurr, leftAnkleRef);

      // Check if left ankle moved (right ankle ignored - may be occluded)
      if (ankleCheck) {
        // Calculate RIGHT wrist distance
        const rightWrist = keypoints[rightWristIdx];
        const rightPixelDiff = Math.abs(rightWrist.x - functionalReach.forward_right_reference_point.rightWrist.x);
        const rightDistance = (rightPixelDiff * functionalReach.averaged_depth) / FX;

        // Update max if this is better
        if (rightDistance > newMaxDistance) {
          newMaxDistance = rightDistance;
        }
      }
    }
  }

  // Update UI with the maximum distance (from either arm)
  reach_distance_metric.forwardReach = newMaxDistance;
  functionalReach.arom_right_low_text.textContent = newMaxDistance.toFixed(2);
  functionalReach.arom_right_low_value.innerText = "inches";

  // Show 0 if no references captured yet
  if (!functionalReach.forward_left_reference_point && !functionalReach.forward_right_reference_point) {
    functionalReach.arom_right_low_text.textContent = "0";
  }
}

export function calc_distance_in_inches(ref, current, depth) {
  if (!ref || !current) return 0;

  // Use only x-axis difference for distance
  let dx = Math.abs(current.x - ref.x);

  // Use focal length (fx) and depth to convert pixel distance to real-world distance in inches
  // real_distance = (pixel_distance * depth) / focal_length
  // Assuming fx and fy are similar, use FX

  return (dx * depth) / FX;
}

export function isWithin25Pixels(p1, p2) {
  const dx = Math.abs(p1?.x - p2?.x);
  const dy = Math.abs(p1?.y - p2?.y);
  return dx <= 25 && dy <= 25;
}

// LIVE CAMERA: More tolerant ankle stability check for functional reach
export function isWithin50Pixels(p1, p2) {
  const dx = Math.abs(p1?.x - p2?.x);
  const dy = Math.abs(p1?.y - p2?.y);
  return dx <= 50 && dy <= 50;
}

// Helper function to check if a single arm is stretched
function checkSingleArm(shoulder, elbow, wrist) {
  if (!shoulder || !elbow || !wrist) return false;

  // 1. Angle between wrist–shoulder and downward vector (for original stretch detection)
  const vectorA = {
    x: wrist.x - shoulder.x,
    y: wrist.y - shoulder.y,
  };
  const vectorB = { x: 0, y: 50 };
  const angle1 = getAngleBetweenVectors(vectorA, vectorB);
  const isStretched = angle1 >= 75 && angle1 <= 105;

  // 2. Angle at elbow (shoulder–elbow–wrist)
  const vec1 = {
    x: shoulder.x - elbow.x,
    y: shoulder.y - elbow.y,
  };
  const vec2 = {
    x: wrist.x - elbow.x,
    y: wrist.y - elbow.y,
  };
  const elbowAngle = getAngleBetweenVectors(vec1, vec2);
  const isArmStraight = elbowAngle >= 160 && elbowAngle <= 200;

  return isStretched && isArmStraight;
}

// New function to detect which arm is stretched for forward reach
export function getStretchedArmForForwardReach(poses) {
  if (!poses || poses.length === 0 || !poses[0] || !poses[0].keypoints) {
    return { left: false, right: false, which: null };
  }

  const keypoints = poses[0].keypoints;
  const indices = getKeypointIndices(keypoints);

  // Get all keypoints for both arms
  const leftShoulder = keypoints[indices.leftShoulder];
  const leftElbow = keypoints[indices.leftElbow];
  const leftWrist = keypoints[indices.leftWrist];

  const rightShoulder = keypoints[indices.rightShoulder];
  const rightElbow = keypoints[indices.rightElbow];
  const rightWrist = keypoints[indices.rightWrist];

  // In profile: One shoulder visible (near side), one occluded (far side)
  // Use the VISIBLE shoulder to check arms using existing checkSingleArm logic

  let leftStretched = false;
  let rightStretched = false;

  // Check left arm using left shoulder (if visible)
  if (leftShoulder) {
    leftStretched = checkSingleArm(leftShoulder, leftElbow, leftWrist);
  }

  // Check right arm using right shoulder (if visible)
  if (rightShoulder) {
    rightStretched = checkSingleArm(rightShoulder, rightElbow, rightWrist);
  }

  // If one shoulder occluded, use the visible shoulder for the occluded arm too
  if (!leftShoulder && rightShoulder && leftElbow && leftWrist) {
    leftStretched = checkSingleArm(rightShoulder, leftElbow, leftWrist);
  }

  if (!rightShoulder && leftShoulder && rightElbow && rightWrist) {
    rightStretched = checkSingleArm(leftShoulder, rightElbow, rightWrist);
  }

  // Pick whichever arm is stretched (prefer left if both)
  const which = leftStretched ? 'left' : (rightStretched ? 'right' : null);

  return {
    left: which === 'left',
    right: which === 'right',
    which: which
  };
}

export function isArmStretched(poses, phase) {
  // TESTING: Safety check for empty poses array
  if (!poses || poses.length === 0 || !poses[0] || !poses[0].keypoints) {
    return false;
  }

  const keypoints = poses[0].keypoints;
  const indices = getKeypointIndices(keypoints);
  let shoulder, elbow, wrist;

  if (phase === FUNCTIONAL_REACH_ASSESSMENT.Left_Reach) {
    // Left reach - only check left arm
    shoulder = keypoints[indices.leftShoulder];
    elbow = keypoints[indices.leftElbow];
    wrist = keypoints[indices.leftWrist];
    return checkSingleArm(shoulder, elbow, wrist);
  } else if (phase === FUNCTIONAL_REACH_ASSESSMENT.Right_Reach) {
    // Right reach - only check right arm
    shoulder = keypoints[indices.rightShoulder];
    elbow = keypoints[indices.rightElbow];
    wrist = keypoints[indices.rightWrist];
    return checkSingleArm(shoulder, elbow, wrist);
  } else if (phase === FUNCTIONAL_REACH_ASSESSMENT.Forward_Reach) {
    // Forward reach - check BOTH arms, use whichever is stretched
    const armStatus = getStretchedArmForForwardReach(poses);
    return armStatus.left || armStatus.right;
  }
  // return isStretched && isArmStraight;
}


export function getAngleBetweenVectors(v1, v2) {
  const dotProduct = v1.x * v2.x + v1.y * v2.y;
  const magV1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
  const magV2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);
  const cosTheta = dotProduct / (magV1 * magV2);
  const angleRadians = Math.acos(Math.max(-1, Math.min(1, cosTheta))); // clamp value
  return angleRadians * (180 / Math.PI); // convert to degrees
}

// Check if patient is in profile position for forward reach
function isPatientInProfilePosition(keypoints, baselineWidth) {
  if (!baselineWidth) {
    return false;
  }

  const leftShoulder = keypoints[5];   // MoveNet left shoulder
  const rightShoulder = keypoints[6];  // MoveNet right shoulder

  // Case 1: One shoulder not visible = likely turned to side = PROFILE
  if (!leftShoulder || !rightShoulder) {
    return true;
  }

  // Case 2: Both visible but width reduced = PROFILE
  const currentWidth = Math.abs(leftShoulder.x - rightShoulder.x);
  const PROFILE_REDUCTION_PERCENT = 0.6; // 60% or less = profile
  const isInProfile = currentWidth <= (baselineWidth * PROFILE_REDUCTION_PERCENT);

  return isInProfile;
}

// Export the foot stability function for testing and reuse
export { checkFootStabilityAndStopIfMoved, isPatientInProfilePosition };
